import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { savedPlacesService } from '../services/savedPlacesService';
import { PlaceItem } from '../types/explore';

interface SavedPlacesContextType {
  savedPlaces: PlaceItem[];
  savedPlaceIds: Set<string>;
  loading: boolean;
  error: string | null;
  savePlace: (place: PlaceItem) => Promise<void>;
  unsavePlace: (placeId: string) => Promise<void>;
  isPlaceSaved: (placeId: string) => boolean;
  loadSavedPlaces: () => Promise<void>;
}

const SavedPlacesContext = createContext<SavedPlacesContextType | undefined>(undefined);

export const useSavedPlaces = () => {
  const context = useContext(SavedPlacesContext);
  if (context === undefined) {
    throw new Error('useSavedPlaces must be used within a SavedPlacesProvider');
  }
  return context;
};

interface SavedPlacesProviderProps {
  children: React.ReactNode;
}

export const SavedPlacesProvider: React.FC<SavedPlacesProviderProps> = ({ children }) => {
  const { authState } = useAuth();
  const [savedPlaces, setSavedPlaces] = useState<PlaceItem[]>([]);
  const [savedPlaceIds, setSavedPlaceIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load saved places when user is authenticated
  const loadSavedPlaces = useCallback(async () => {
    // Wait for auth to finish loading before making decisions
    if (authState.loading) {
      return;
    }
    
    if (!authState.isAuthenticated || !authState.user?.userId) {
      setSavedPlaces([]);
      setSavedPlaceIds(new Set());
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const places = await savedPlacesService.getSavedPlaces(authState.user.userId);
      setSavedPlaces(places);
      setSavedPlaceIds(new Set(places.map(place => place.id)));
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load saved places');
    } finally {
      setLoading(false);
    }
  }, [authState.loading, authState.isAuthenticated, authState.user?.userId]);

  // Save a place
  const savePlace = useCallback(async (place: PlaceItem) => {
    if (!authState.isAuthenticated || !authState.user?.userId) {
      throw new Error('User not authenticated');
    }

    try {
      await savedPlacesService.savePlace(authState.user.userId, place);
      
      // Update local state
      setSavedPlaces(prev => [...prev, place]);
      setSavedPlaceIds(prev => new Set(Array.from(prev).concat(place.id)));
      
    } catch (err) {
      throw err;
    }
  }, [authState.isAuthenticated, authState.user?.userId]);

  // Unsave a place
  const unsavePlace = useCallback(async (placeId: string) => {
    if (!authState.isAuthenticated || !authState.user?.userId) {
      throw new Error('User not authenticated');
    }

    try {
      await savedPlacesService.unsavePlace(authState.user.userId, placeId);
      
      // Update local state
      setSavedPlaces(prev => prev.filter(place => place.id !== placeId));
      setSavedPlaceIds(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(placeId);
        return newSet;
      });
      
    } catch (err) {
      throw err;
    }
  }, [authState.isAuthenticated, authState.user?.userId]);

  // Check if a place is saved
  const isPlaceSaved = useCallback((placeId: string): boolean => {
    return savedPlaceIds.has(placeId);
  }, [savedPlaceIds]);

  // Load saved places when user authentication changes
  useEffect(() => {
    loadSavedPlaces();
  }, [loadSavedPlaces]);

  const value: SavedPlacesContextType = {
    savedPlaces,
    savedPlaceIds,
    loading,
    error,
    savePlace,
    unsavePlace,
    isPlaceSaved,
    loadSavedPlaces
  };

  return (
    <SavedPlacesContext.Provider value={value}>
      {children}
    </SavedPlacesContext.Provider>
  );
};
